import RightRail, { Content, Footer, Header, TripleAction, Confirmation } from '../../../libs/gs-component-right-rail';
import { generateMessagingPath } from '../../../helpers/generateMessagingPath';
import Spinner from '@xo-union/tk-component-spinner';
import { useFormikContext } from 'formik';
import React, { useMemo, useState, useEffect } from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { Select, Option } from '@xo-union/tk-component-fields';

import { ShareValues } from '.';
import { SmsBubble } from '../../MessagingHub/SmsPreview/SmsBubble';
import styles from '../rightRails.scss';
import useShareTracking from '../useShareTracking';
import {
  MessagingSequencesQuery,
  useMessagingSequencesQuery,
  useWeddingWebsiteQuery,
} from '../../../generated/graphql';

import {
  getSequences,
  getSaveTheDateEligibility,
  SAVE_THE_DATE_MEDIA_ID,
  getSequenceMap,
  Sequence,
  SEQUENCE_TYPE_LOCAL_HOTELS,
  SEQUENCE_TYPE_RSVP_SCHEDULE,
  SEQUENCE_TYPE_SAVE_THE_DATE,
  SEQUENCE_TYPE_CUSTOM_MESSAGE,
} from '../../MessagingHub/messagingHubHelper';
import WeddingInfoModal from '../../MessagingHub/WeddingInfoModal';
import { useMobileMedia } from '@xo-union/tk-ui-breakpoints';
import { messagingHubRoute } from '../../../helpers/routes';
import InfoCard from '../../MessagingHub/InfoCard';
import { LOOKING_FOR_HOTELS_INFO_CARD } from '../helper';

export type Sequences = MessagingSequencesQuery['messagingSequences'];

interface Props {
  matchPath: string;
  loading: boolean;
  smsLink?: string;
  displayName?: string;
}

const LOCAL_HOTEL_SMS_SEQUENCE_ID = '6d7fe6b3-a268-4fce-8ccb-69264b423f56';

const SmsEditorRail: React.FC<Props> = ({ matchPath, loading, smsLink, displayName = 'Share Your Website' }) => {
  const { data } = useMessagingSequencesQuery();
  const { data: gdsData, loading: gdsLoading } = useWeddingWebsiteQuery();
  const { values, dirty, isValid, handleReset } = useFormikContext<ShareValues>();
  const match = useRouteMatch(matchPath);
  const history = useHistory();
  const { pathname } = useLocation();
  const parts = pathname.split('/');

  const sequences = useMemo(() => getSequences(data, gdsData), [gdsData, data]);
  const sequenceMap: Map<string, string> = useMemo(() => getSequenceMap(sequences, 'sms'), [sequences]);

  const [currentSequenceId, setCurrentSequenceId] = useState(parts[parts.length - 2]);
  const [showModal, setShowModal] = useState(false);
  const [showAbandonChangesConfirmation, setShowAbandonChangesConfirmation] = useState(false);
  const [targetSequenceId, setTargetSequenceId] = useState('');
  const mobileMedia = useMobileMedia();

  const track = useShareTracking();

  const doneHandler = () => {
    if (isValid) {
      if (dirty) {
        track('WWS Share Interaction', { action: 'edit message' });
      }
      const nextLoc = pathname.replace('/edit', '');
      history.push(nextLoc);
    }
  };
  const cancelHandler = () => {
    history.push(messagingHubRoute);
  };

  function handleSequenceChangeHelper(selectedSequenceId: string) {
    const { member, wedding } = gdsData ?? {};
    const mediaId = sequenceMap.get(selectedSequenceId);

    if (SAVE_THE_DATE_MEDIA_ID === mediaId && !getSaveTheDateEligibility(member, wedding)) {
      setShowModal(true);
      setTargetSequenceId(selectedSequenceId);
    } else {
      setShowModal(false);
      setCurrentSequenceId(selectedSequenceId);
    }

    if (SAVE_THE_DATE_MEDIA_ID === mediaId) {
      //Save the date selection tracking
      track('Save the Date Interaction', {
        action: 'Save the Date selected',
        messageType: 'Save the date',
        medium: 'sms',
      });
    }

    handleReset();
  }
  function handleSequenceChange(event: any) {
    let sequenceId: string = event.option.value;
    if (!dirty) {
      handleSequenceChangeHelper(sequenceId);
    } else {
      setShowAbandonChangesConfirmation(true);
      setTargetSequenceId(sequenceId);
    }
  }

  function onCancel() {
    setShowAbandonChangesConfirmation(false);
  }

  function onConfirm() {
    handleSequenceChangeHelper(targetSequenceId);
    setShowAbandonChangesConfirmation(false);
  }

  function handleSubmit(sequenceId: string) {
    setShowModal(false);
    setCurrentSequenceId(sequenceId);
  }

  useEffect(() => {
    sequences.active.forEach((s: Sequence) => {
      if (s?.supportedTypes?.find((b: { type: string }) => b.type === 'sms')?.id === currentSequenceId) {
        const to = generateMessagingPath(s.sequenceType, currentSequenceId, 'sms');
        history.push(to);
      }
    });
  }, [currentSequenceId, history, sequences]);

  const headerProps = mobileMedia.yes ? { leftButtonText: 'Back', leftButtonOnClick: cancelHandler } : {};

  return (
    <RightRail open={!!match} overlay={false} className={styles.rightRail}>
      <Header {...headerProps}>New text message</Header>
      {!showAbandonChangesConfirmation ? (
        <>
          <Content className={styles.editorContainer}>
            {(loading || gdsLoading) && !data && !currentSequenceId ? (
              <div></div>
            ) : (
              <div className={styles.templateContainer}>
                <Select name="Template" value={currentSequenceId} onChange={(e: any) => handleSequenceChange(e)}>
                  {sequences.active
                    .map(s => {
                      // TODO: Delete After test
                      if (
                        s.sequenceType === SEQUENCE_TYPE_RSVP_SCHEDULE ||
                        s.sequenceType === SEQUENCE_TYPE_SAVE_THE_DATE ||
                        s.sequenceType === SEQUENCE_TYPE_CUSTOM_MESSAGE
                      ) {
                        return null;
                      }

                      let displayName = s.displayName;
                      if (s.sequenceType === SEQUENCE_TYPE_LOCAL_HOTELS) {
                        displayName = 'Share your hotel block';
                      }
                      const smsSeqId = s?.supportedTypes?.find(b => b.type === 'sms')?.id;

                      // Only return the option if we have a valid smsSeqId
                      if (!smsSeqId) {
                        console.log(s);
                        return null;
                      }

                      return {
                        key: smsSeqId,
                        value: smsSeqId,
                        displayName,
                      };
                    })
                    .filter((optionData): optionData is NonNullable<typeof optionData> => optionData !== null)
                    .map(optionData => (
                      <Option key={optionData.key} value={optionData.value} label={optionData.displayName}>
                        {optionData.displayName}
                      </Option>
                    ))}
                </Select>
              </div>
            )}
            {loading ? (
              <Spinner />
            ) : (
              <>
                {showModal ? (
                  <WeddingInfoModal
                    onSubmit={sequenceId => handleSubmit(sequenceId as string)}
                    onClose={() => setShowModal(false)}
                    sequence={targetSequenceId}
                  />
                ) : null}

                <SmsBubble message={values.greeting ? values.greeting : ''} color="secondary" />
                <SmsBubble message={values.message ? values.message : ''} color="secondary" />
                {smsLink ? <SmsBubble message={smsLink} color="secondary" /> : null}
                <SmsBubble message={values.optout ? values.optout : ''} color="secondary" />
              </>
            )}
            {currentSequenceId === LOCAL_HOTEL_SMS_SEQUENCE_ID ? (
              <div className={styles.localHotelInfoCard}>
                <InfoCard
                  {...LOOKING_FOR_HOTELS_INFO_CARD}
                  onClick={(track: ReturnType<typeof useShareTracking>) =>
                    LOOKING_FOR_HOTELS_INFO_CARD.onClick(track, 'sms')
                  }
                />
              </div>
            ) : null}
          </Content>
          <Footer>
            <TripleAction
              primaryOnClick={doneHandler}
              primaryText="Add Recipients"
              primaryDisabled={!isValid}
              secondaryOnClick={cancelHandler}
              secondaryText="Cancel"
            />
          </Footer>
        </>
      ) : (
        <Confirmation
          data-testid="changes-confirmation"
          question="Are you sure?"
          info="Any unsaved changes will be lost."
          primaryText="Yes"
          onPrimaryAction={onConfirm}
          secondaryText="No"
          onSecondaryAction={onCancel}
          showSpinner={loading}
        />
      )}
    </RightRail>
  );
};

export default SmsEditorRail;
